import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { Storage } from '@ionic/storage';
import { Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { BasicService } from '../../services/basic.service';
import { Choice } from './choice.model';
/**
 * This component allows to display a select (single/multiple) that embed these options:
 * - the `control`, that represents the model and defines if the select is required, disabled etc (refer to FormControl);
 *                It also represents the value in the search input field.
 * - the `label`, displayed as a float label;
 * - the `labelKey`, translation key, displayed as a float label;
 * - the `items`, a list of Choice proposed to be selected;
 * - the `reverseSort`, a flag to reverse or not the list. Consider that the list is already sorted*;
 * - the `sortKey`, a key used to sort the list based on a property that enriched the Choice;
 * - the `localStorageKey`, a key used to remember the user's selection for the next time;
 * - the `enableTypeahead`, a flag to add a search input field and help to search in the items, to use when `multiple` is false;
 * - the `disableMultipleTypeahead`, a flag to remove the search input field, when multiple `true`;
 * - the `onChange`, a callback function that is triggered when the user type something into the search input field. It is optional;
 * - the `onClose`, a callback function that is triggered in case of selection change. It is optional;
 * - the `multiple`, flag to change the list as multiple;
 * - the `optionTemplateName`, a common template name reference to use to customize the options;
 * - the `noFloatingLabel`, a flag to hide the label when choice(s) are selected.
 * - the `showEmptyOption`, a flag to show an option that get the value undefined. Only for simple select.
 * /!\ The examples below are described for angularjs integration, use `app-select` component and the correct inputs type in angular.
 *
 * @example
 *    <angular-select
 *      [control]="myFormControl"
 *      [items]="items"
 *      [label]="Hello world!">
 *    </angular-select>
 *
 * @example
 *     <angular-select
 *       [control]="myFormControl"
 *       [items]="items"
 *       [enable-typeahead]="true",
 *       (on-change)="getAsyncItemsFromSearch()">
 *     </angular-select>
 *
 * @example
 *     <angular-select
 *       [control]="myFormControl"
 *       [items]="items"
 *       [itemsMap]="itemsMap"
 *       [multiple]="true"
 *       (on-change)="getAsyncItemsFromSearch()">
 *     </angular-select>
 *
 * @example
 *     <angular-select
 *       [control]="myFormControl"
 *       [items]="items"
 *       [reverse-sort]="true"
 *       [local-storage-key]="'my-local-storage-key'"
 *       (on-close)="doSomethingAfterSelection()">
 *     </angular-select>
 */
@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SelectComponent implements OnInit, OnDestroy {
  @Input() control: FormControl | AbstractControl = new FormControl();
  @Input() appearance?: 'fill' | 'outline';
  @Input() identifier = 'id';
  @Input() label = '';
  @Input() labelKey = '';
  @Input() reverseSort: boolean;
  @Input() sortKey: string;
  @Input() localStorageKey: string;
  @Input() enableTypeahead: boolean;
  @Input() ionicIcons: boolean; // implemented only for select with typeahead.
  @Input() items: Choice[];
  @Input() panelClass: string | string[] = '';
  @Input() formFieldClass: string;
  @Input() multiple: boolean;
  @Input() itemsMap: Map<string, Choice>;
  @Input() disableMultipleTypeahead = false;
  @Input() noFloatingLabel = false;
  @Input() optionTemplateName: 'site-icon'; // By default it is a text and an iconClass if exists.
  @Input() showEmptyOption = false;

  @Output() triggerClose: EventEmitter<void> = new EventEmitter();
  @Output() triggerChange: EventEmitter<void> = new EventEmitter();

  private updated: boolean;
  searchControl = new FormControl('');
  filteredOptions: Observable<Choice[]>;
  private subscriptions = new Subscription();
  @ViewChild('iconClassAndText', { static: true }) iconClassAndText: TemplateRef<any>;
  @ViewChild('siteIconAndText', { static: true }) siteIconAndText: TemplateRef<any>;

  templateRef: TemplateRef<any>;
  itemsSelectedPreview = [];
  keysSelected: string[];
  keysSelectedSet: Set<string> = new Set();

  constructor(private basicService: BasicService, private storage: Storage) {}

  async ngOnInit() {
    this.templateRef = this.optionTemplateName === 'site-icon' ? this.siteIconAndText : this.iconClassAndText;
    if (!this.itemsMap && (this.multiple || this.enableTypeahead)) {
      this.itemsMap = this.items.reduce((result, item) => {
        result.set(item[this.identifier], item);
        return result;
      }, new Map());
    }
    this.addValidators();

    if (this.enableTypeahead && this.multiple) {
      const label = this.displayFn(this.control.value);
      this.control.setValue(label);
    }

    if (this.sortKey) {
      this.items = this.items.sort((a, b) => a[this.sortKey].localeCompare(b[this.sortKey]));
    }
    if (this.reverseSort) {
      this.items = this.items.reverse();
    }

    if (this.labelKey) {
      this.label = this.basicService.translate(this.labelKey);
    }

    await this.loadFromLocalStorage();

    this.itemsSelectedPreview = this.multiple
      ? this.items.filter((i: Choice) => this.control.value.indexOf(i[this.identifier]) > -1)
      : [this.items.find((i: Choice) => this.control.value === i[this.identifier])];

    if (this.multiple) {
      this.keysSelected = this.control.value;
      this.keysSelectedSet = new Set(this.control.value);
      this.filteredOptions = this.searchControl.valueChanges.pipe(
        startWith(''),
        map(value => this._filter(value || ''))
      );
      this.subscriptions.add(
        this.control.valueChanges.subscribe(value => {
          (value || []).forEach(v => this.keysSelectedSet.add(v));

          this.keysSelected = value;
          if (this.control.disabled) {
            this.itemsSelectedPreview = this.items.filter(
              (i: Choice) => this.control.value.indexOf(i[this.identifier]) > -1
            );
          }
        })
      );
      this.panelClass = ['select-complex'].concat(this.panelClass);
    } else {
      this.filteredOptions = this.control.valueChanges.pipe(
        startWith(''),
        map(value => this._filter(value || ''))
      );
      this.subscriptions.add(
        this.control.valueChanges.subscribe(value => {
          this.itemsSelectedPreview = [this.items.find((i: Choice) => value === i[this.identifier])];
          this.saveToLocalStorage();
          this.triggerChange.emit();
        })
      );
    }

    // multiple or simple
    if (!this.enableTypeahead) {
      this.formFieldClass = [this.optionTemplateName, this.formFieldClass].join(' ').trim();
    }
  }

  openedChange(opened: boolean) {
    if (!opened) {
      this.searchControl.setValue('');
      if (this.multiple && this.updated) {
        this.control.setValue(this.keysSelected);
        this.control.markAsDirty();
        this.updated = false;
      }
      this.triggerClose.emit();
    }
  }

  displayFn = value => this.items?.find(item => item[this.identifier] === value)?.label;

  private _filter(value: string): Choice[] {
    const filterValue = (this.displayFn(value) || value).toLowerCase();
    return this.items.filter(option => option?.label.toLowerCase().includes(filterValue.toLowerCase()));
  }

  private async loadFromLocalStorage() {
    if (this.localStorageKey) {
      let value = await this.storage.get(this.localStorageKey);
      if (value) {
        if (this.multiple) {
          if (!Array.isArray(value)) {
            value = [value];
          }
          if (value.length) {
            // Removes items from the caches that no longer exist.
            value = value.filter(i => this.itemsMap.has(i));
            await this.saveToLocalStorage(value);
          }
        }

        this.control.setValue(value);
      }
    }
  }

  private async saveToLocalStorage(values?) {
    if (this.localStorageKey) {
      return this.storage.set(this.localStorageKey, values || this.control.value);
    }
  }

  /**
   * Verifies that the `Choice` is included into the `items`.
   */
  private addValidators() {
    const choiceValidator: ValidatorFn = (control: FormControl): { [key: string]: any } | null => {
      const { value } = control;
      if (this.multiple && Array.isArray(value)) {
        const match = value.every(v => this.items.find(item => item[this.identifier] === v));
        return match ? null : { unknown: true };
      }

      if ((!value || !value.length) && !control.hasValidator(Validators.required)) {
        return null;
      }

      const matches = this.items.find(item => item[this.identifier] === value);

      return matches ? null : { unknown: true };
    };

    this.control.addValidators(choiceValidator);
  }

  selectItem($event) {
    if ($event) {
      const { isUserInput, source } = $event;
      if (isUserInput) {
        const { value, selected } = source;

        if (selected) {
          this.keysSelectedSet.add(value);
        } else {
          this.keysSelectedSet.delete(value);
        }

        this.updated = true;
        this.keysSelected = Array.from(this.keysSelectedSet);
        this.itemsSelectedPreview = this.items.filter((i: Choice) => this.keysSelectedSet.has(i[this.identifier]));

        this.saveToLocalStorage(this.keysSelected);
      }
    }
  }

  selectAll() {
    this.updateKeysSelected(Array.from(this.itemsMap.keys()));
  }

  unselectAll() {
    this.updateKeysSelected([]);
  }

  private updateKeysSelected(keys: string[]) {
    this.updated = true;
    this.keysSelected = Array.from(keys);
    this.keysSelectedSet = new Set(keys);
    this.itemsSelectedPreview = this.items.filter((i: Choice) => this.keysSelectedSet.has(i[this.identifier]));
    this.saveToLocalStorage(this.keysSelected);
  }

  ngOnDestroy() {
    // Way to remove only the custom validator added and avoid a leak memory.
    this.control.removeValidators((this.control as any)._rawValidators?.find(v => v.name === 'choiceValidator'));
    this.subscriptions?.unsubscribe();
  }
}
